import React, { useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from 'react';
import {
  TablePaginatedV8,
  order2Sorting,
  TableCellV8,
  EditCellV8,
  TableDataState,
  promiseModalDialog,
  toastSuccess,
  CellDateV8,
  EyeColumnV8,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { gridInactiveRowStyle, gridRowSelectedStyle } from 'shared-pages/common/grid-helpfunc';
import * as Lo from 'lodash';
import { FormInput, gqlAdd, gqlDelete, gqlUpdate } from '../graphql';
import { useMutation } from '@apollo/client';
import { getRouteByName } from 'app/routes';

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
  } = props;
  const history = useHistory();
  const location = useLocation<any>();
  const { t } = useTranslation();
  const [updateProduction] = useMutation(gqlUpdate);
  const [addNewProduction] = useMutation(gqlAdd);
  const [deleteProduction] = useMutation(gqlDelete);
  const { appStore, tableStore } = useRootStore();
  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('Production'), { id: row.id }),
      });
    },
    [history, location.pathname, location.state],
  );

  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'id',
        header: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: TableCellV8,
        size: 5,
      },
      {
        id: 'code',
        header: t('Κωδικός Παραγωγής'),
        accessorKey: 'code',
        orderByField: 'code',
        cell: TableCellV8,
        size: 20,
      },
      {
        id: 'productionDate',
        header: t('Ημερομηνία Παραγωγής'),
        accessorKey: 'productionDate',
        orderByField: 'productionDate',
        cell: CellDateV8,
        size: 20,
      },

      {
        id: 'fromWastereceptionpointId',
        header: t('Προέλευση'),
        columnName: t('Προέλευση'),
        accessorKey: 'fromWastereceptionpoint.name',
        orderByField: 'fromWastereceptionpoint.name',
        cell: ({ getValue, row }) => (
          <div className="text-left">
            <div>{row.original.fromWastereceptionpoint.name}</div>
            <div style={{ color: 'gray', fontSize: 'smaller' }}>
              {row.original.fromWastereceptionpoint.addressLine1}
              ,&nbsp;{row.original.fromWastereceptionpoint.addressArea}
              ,&nbsp;{row.original.fromWastereceptionpoint.addressCity}
            </div>
          </div>
        ),
        size: 35,
        meta: { type: 'text' },
      },
      {
        id: 'productionLines',
        header: t('Απόβλητα'),
        columnName: t('Απόβλητα'),
        accessorKey: 'internalTransactionLines',
        orderByField: 'internalTransactionLines',
        cell: ({ getValue, row }) => {
          function compare(a, b) {
            if (a.aa < b.aa) {
              return -1;
            }
            if (a.aa > b.aa) {
              return 1;
            }
            return 0;
          }
          const lines = row.original.productionLines || [];
          return (
            <div className="text-left">
              {lines.sort(compare).map((v, i) => {
                const producedMaterials = v.producedMaterials || [];
                return (
                  <div key={i + v.aa}>
                    {v.aa ?? 0}: {v.material.name} ({v.qty} ΤΕΜ) {v.netWeight} kg
                    {producedMaterials.length > 0 &&
                      producedMaterials.map((pm, pi) => (
                        <div
                          key={i + v.aa + '-pm-' + pi}
                          className="mt-1"
                          style={{
                            marginLeft: '15px',
                            fontStyle: 'italic',
                            color: 'gray',
                            fontSize: 'smaller',
                          }}
                        >
                          - {pm.material.name}: {pm.netWeight} kg
                        </div>
                      ))}
                  </div>
                );
              })}
            </div>
          );
        },
        size: 35,
      },
      {
        id: 'notes',
        header: t('Παρατηρήσεις'),
        accessorKey: 'notes',
        orderByField: 'notes',
        cell: TableCellV8,
        size: 20,
      },
      {
        id: 'edit',
        header: t('Ενέργειες'),
        accessorKey: 'edit',
        size: 10,
        cell: (props) => EditCellV8({ row: props.row, table: props.table }),
      },
    ],
    [t],
  );

  const tableRef = useRef(null);
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          id="production-grid"
          key={'grid'}
          appStore={appStore}
          tableStore={tableStore}
          loading={loading}
          onExport={handleExport}
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          onPageChange={onPageChange}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          initSorting={order2Sorting(initOrderBy)}
          initPageIndex={initPageIndex}
          onSortByChange={(sort) => {
            const order = `${sort[0].id} ${sort[0].desc ? 'DESC' : 'ASC'}`;
            onOrderChange(order);
          }}
          onNewRow={() => ({ id: '*', isActive: true })}
          onSaveRow={async (rowIndex, row, changes) => {
            let id = row.id;
            const values = { id, ...changes };
            const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
            if (tableStore.state === TableDataState.insert) {
              mutValues.id = null;
              const res = await addNewProduction({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false;
              id = res.data.addProduction.id;
            } else if (tableStore.state === TableDataState.edit) {
              const res = await updateProduction({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false;
            }
            refetch();
            return id;
          }}
          deleteRow={async (row) => {
            const ans = await promiseModalDialog({
              title: 'Delete Record',
              text: `Are you sure you want to delete <strong>${row.name}</strong>?`,
            });
            if (!ans) {
              return;
            }
            const res = await deleteProduction({ variables: { id: row.id } }).catch((reason) => {
              console.error('$$$ grid.Delete error reason', reason);
            });
            if (!res) {
              return false;
            }
            refetch();
            toastSuccess('Record deleted successfully');
            return true;
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
